import { Button, Flex, Form, FormProps, Modal, ModalProps, Tooltip } from 'antd';
import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface EditableModalConfig {
  title: ReactNode;
  content?: ReactNode;
  form?: EditableModalFormConfig;
}

interface EditableModalFormConfig extends Omit<FormProps, 'children' | 'onFinish'> {
  children: ReactNode;
  onFinish: (
    values: any,
    helpers: { closeModal: () => void },
  ) => Promise<void | false> | void | false;
  saveDisabled?: boolean;
  saveTooltip?: ReactNode;
  submitText?: ReactNode;
  cancelText?: ReactNode;
}

interface EditableModalProps<ModalKey extends string> extends Omit<
  ModalProps,
  'open' | 'onCancel' | 'title' | 'children'
> {
  activeModal?: ModalKey;
  closeModal: () => void;
  modalConfigByKey: Record<ModalKey, EditableModalConfig>;
}

// Metadata: centraliza el estado para modales de edición mutuamente excluyentes.
export const useEditableModal = <ModalKey extends string>() => {
  const [activeModal, setActiveModal] = useState<ModalKey>();
  const openModal = useCallback((modalKey: ModalKey) => setActiveModal(modalKey), []);
  const closeModal = useCallback(() => setActiveModal(undefined), []);

  return {
    activeModal,
    openModal,
    closeModal,
  };
};

// Metadata: renderiza el modal activo y deja el formulario concreto en manos del llamador.
const EditableModal = <ModalKey extends string>(props: EditableModalProps<ModalKey>) => {
  const { t } = useTranslation();
  const { activeModal, closeModal, modalConfigByKey, ...modalProps } = props;
  const activeModalConfig = activeModal ? modalConfigByKey[activeModal] : undefined;
  const formConfig = activeModalConfig?.form;
  const handleFormFinish = useCallback(
    async (values: any) => {
      if (!formConfig) return;

      const result = await formConfig.onFinish(values, { closeModal });

      if (result !== false) {
        closeModal();
      }
    },
    [closeModal, formConfig],
  );
  const formContent = formConfig
    ? (() => {
        const {
          children,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onFinish,
          saveDisabled,
          saveTooltip,
          submitText,
          cancelText,
          ...antdFormProps
        } = formConfig;

        return (
          <Form
            {...antdFormProps}
            layout={antdFormProps.layout ?? 'vertical'}
            onFinish={v => void handleFormFinish(v)}>
            {children}
            <Flex className="ModalFormActions" justify="end" gap={8}>
              <Button onClick={closeModal}>{cancelText ?? t('Cancelar')}</Button>
              <Tooltip title={saveTooltip}>
                <Button htmlType="submit" type="primary" disabled={saveDisabled}>
                  {submitText ?? t('Guardar')}
                </Button>
              </Tooltip>
            </Flex>
          </Form>
        );
      })()
    : activeModalConfig?.content;

  return (
    <Modal
      destroyOnHidden
      footer={null}
      {...modalProps}
      open={!!activeModalConfig}
      onCancel={closeModal}
      title={activeModalConfig?.title}>
      {formContent}
    </Modal>
  );
};

export default EditableModal;
